// Shared state for a project's document links. Server is authoritative for
// authorship, permissions and the three-key limit; the client only blocks the
// obvious fourth star and serializes its own star requests so a double click
// cannot race. Failed or denied writes never appear successful.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getProjectDocuments, getProjectDocumentAccess, createProjectDocument, updateProjectDocument,
  deleteProjectDocument, emitProjectDocumentsChanged, onProjectDocumentsChanged,
} from "../../../data/projectsClient.js";

export const MAX_KEY_DOCUMENTS = 3;

const bySort = (a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
  || String(a.addedAt || "").localeCompare(String(b.addedAt || "")) || String(a.id).localeCompare(String(b.id));

export function keyDocuments(items = []) {
  return items.filter((d) => d.isKey === true).sort(bySort).slice(0, MAX_KEY_DOCUMENTS);
}

export function useProjectDocuments(projectId, { enabled = true } = {}) {
  const [state, setState] = useState({ status: enabled ? "loading" : "idle", items: [], error: null });
  const [canManage, setCanManage] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const pending = useRef(false);
  const mounted = useRef(true);
  const source = useRef(`docs-${Math.random().toString(36).slice(2)}`);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = useCallback((quiet = false) => {
    if (!enabled || !projectId) return;
    const mine = ++seq.current;
    if (!quiet) { setState({ status: "loading", items: [], error: null }); setCanManage(false); }
    Promise.resolve().then(() => getProjectDocuments(projectId)).then((rows) => {
      if (!Array.isArray(rows)) throw new Error("The server returned an invalid document list.");
      if (mounted.current && mine === seq.current) setState({ status: "ok", items: rows, error: null });
    }).catch((err) => {
      if (!mounted.current || mine !== seq.current) return;
      if (quiet) setError(err?.message || "Could not refresh documents.");
      else setState({ status: "error", items: [], error: err?.message || "Could not load documents." });
    });
    Promise.resolve().then(() => getProjectDocumentAccess(projectId))
      .then((a) => { if (mounted.current && mine === seq.current) setCanManage(a.canManage === true); })
      .catch(() => { if (mounted.current && mine === seq.current) setCanManage(false); });
  }, [projectId, enabled]);

  useEffect(() => {
    if (!enabled) { setState({ status: "idle", items: [], error: null }); setCanManage(false); return undefined; }
    setError(null);
    load();
    setCanManage(false);
    const off = onProjectDocumentsChanged(projectId, (detail) => { if (detail?.source !== source.current) load(true); });
    // Other sessions: refresh when the tab regains focus/visibility, plus a
    // finite 60s poll while visible. Skipped during our own writes.
    const refresh = () => { if (!pending.current && (typeof document === "undefined" || document.visibilityState !== "hidden")) load(true); };
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVis);
    const timer = window.setInterval(refresh, 60000);
    return () => { off(); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", onVis); window.clearInterval(timer); };
  }, [projectId, enabled, load]);

  const run = useCallback(async (id, fn) => {
    if (pending.current) return false;
    pending.current = true; setBusyId(id); setError(null);
    try {
      await fn();
      emitProjectDocumentsChanged(projectId, source.current);
      return true;
    } catch (err) {
      if (mounted.current) setError(err?.message || "The document change was not saved.");
      return false;
    } finally {
      pending.current = false;
      if (mounted.current) { setBusyId(null); load(true); }
    }
  }, [projectId, load]);

  const add = useCallback(({ url, title }) => run("new", () => createProjectDocument(projectId, { url, title, source: "Google Drive" })), [projectId, run]);
  const remove = useCallback((doc) => run(doc.id, () => deleteProjectDocument(projectId, doc.id)), [projectId, run]);
  const keys = useMemo(() => keyDocuments(state.items), [state.items]);
  const toggleKey = useCallback((doc) => {
    if (!doc.isKey && keys.length >= MAX_KEY_DOCUMENTS) {
      setError(`Up to ${MAX_KEY_DOCUMENTS} key documents. Unstar one first.`);
      return Promise.resolve(false);
    }
    return run(doc.id, () => updateProjectDocument(projectId, doc.id, { isKey: !doc.isKey }));
  }, [projectId, run, keys.length]);

  const items = useMemo(() => [...state.items].sort(bySort), [state.items]);
  return { ...state, items, keys, canManage, busyId, error, setError, add, remove, toggleKey, retry: () => load() };
}
